import { openUniversalTransferDocumentCreateForm } from "components/finances/documentCreation";
import DocumentListFilters, {
    DocumentListCounter,
    withDisabledTableControls,
} from "components/finances/documentListFilters";
import {
    getDocumentUpdateSubscriptionKey,
    subscribeToDocumentUpdates,
} from "components/finances/documentUpdateSubscription";
import BookIndex from "components/shared/book/list";
import { Transition } from "components/shared/column";
import InlineBtn from "components/shared/inlineBtn";
import SharedReactTable from "components/shared/reactTable";
import { dateTimeToView } from "helpers/date";
import { bidStatuses, universalTransferDocumentModes, universalTransferDocumentTypes } from "helpers/enums";
import { paths } from "helpers/paths";
import {
    BooleanColumnFilter,
    DateRangeColumnFilter,
    DebouncedRangeColumnFilter,
    rowIndexColumn,
    SelectColumnFilter,
} from "helpers/reactTable";
import { tableContainer } from "helpers/redux";
import { enumLabel, formatNumber } from "helpers/system";
import React from "react";
import { Link } from "react-router-dom";
import { namesList as counterpartiesList } from "sources/books/cargoOwners";
import CreateUniversalTransferDocumentForm from "./createForm";

const pendingManualUpdateOptions = [
    { label: "Показать все", value: "all" },
    { label: "Требует ручной проверки", value: "true" },
    { label: "Не требует ручной проверки", value: "false" },
];

class UniversalTransferDocumentsList extends BookIndex {
    documentUpdateSubscriptions = {};

    isUnmounted = false;

    get tableColumns() {
        return [
            rowIndexColumn,
            {
                Header: "Номер",
                accessor: "number",
                filterable: true,
                Cell: ({ value, row: { original } }) =>
                    original.documentExists ? (
                        <Link to={paths.universalTransferDocumentsShow(original.id)}>
                            {value || original.displayNumber || original.id}
                        </Link>
                    ) : (
                        <>
                            <span className="text-muted">УПД не создан</span>
                            {this.props.canEditBooks && this.renderCreateDocumentButton(original)}
                        </>
                    ),
            },
            {
                Header: "Заявка",
                accessor: "bidId",
                Cell: ({ value, row: { original } }) => {
                    const bidId = value || original.primaryUniversalDocument?.relatedToId;

                    return bidId ? <Link to={paths.bidShow("bid", bidId)}>Заявка: {bidId}</Link> : null;
                },
            },
            {
                Header: "Статус заявки",
                accessor: "bidStatus",
                Cell: ({ value }) => enumLabel(bidStatuses, value),
            },
            {
                Header: "Создан",
                accessor: "createdAt",
                filterable: true,
                filterType: "dateTimeRange",
                sortable: true,
                Filter: DateRangeColumnFilter,
                Cell: ({ value }) => dateTimeToView(value),
                CSVExport: ({ value }) => dateTimeToView(value),
            },
            {
                Header: "Статус УПД",
                accessor: "type",
                filterable: true,
                filterType: "enum",
                Filter: (props) => (
                    <SelectColumnFilter
                        {...props}
                        selectProps={{ inputProps: { name: "type" }, options: universalTransferDocumentTypes }}
                    />
                ),
                Cell: ({ value }) => enumLabel(universalTransferDocumentTypes, value),
                CSVExport: ({ value }) => enumLabel(universalTransferDocumentTypes, value),
            },
            {
                Header: "Режим",
                accessor: "mode",
                filterable: true,
                filterType: "enum",
                Filter: (props) => (
                    <SelectColumnFilter
                        {...props}
                        selectProps={{ inputProps: { name: "mode" }, options: universalTransferDocumentModes }}
                    />
                ),
                Cell: ({ value }) => enumLabel(universalTransferDocumentModes, value),
                CSVExport: ({ value }) => enumLabel(universalTransferDocumentModes, value),
            },
            {
                Header: "Контрагент",
                accessor: "counterpartyId",
                filterable: true,
                filterType: "select",
                Filter: (props) => (
                    <SelectColumnFilter
                        {...props}
                        selectProps={{
                            url: counterpartiesList,
                            inputProps: { name: "counterpartyId" },
                            propName: "name",
                        }}
                    />
                ),
                Cell: ({ row: { original } }) => original.counterparty?.displayName || original.counterparty?.name,
                CSVExport: ({ original }) => original.counterparty?.displayName || original.counterparty?.name,
            },
            {
                Header: "Сумма без НДС",
                accessor: "totalWithoutVat",
                filterable: true,
                filterType: "numberRange",
                Filter: DebouncedRangeColumnFilter,
                Cell: ({ value, row: { original } }) => (original.documentExists ? formatNumber(value) : null),
            },
            {
                Header: "НДС",
                accessor: "totalVat",
                filterable: true,
                filterType: "numberRange",
                Filter: DebouncedRangeColumnFilter,
                Cell: ({ value, row: { original } }) =>
                    original.documentExists
                        ? value === null || value === undefined
                            ? "Без НДС"
                            : formatNumber(value)
                        : null,
            },
            {
                Header: "Сумма с НДС",
                accessor: "total",
                filterable: true,
                filterType: "numberRange",
                Filter: DebouncedRangeColumnFilter,
                Cell: ({ value, row: { original } }) =>
                    original.documentExists ? formatNumber(value ?? original.totalWithVat) : null,
            },
            {
                Header: "Проверить вручную",
                accessor: "isPendingManualUpdate",
                filterable: true,
                filterType: "boolean",
                Filter: (props) => <BooleanColumnFilter {...props} options={pendingManualUpdateOptions} />,
                Cell: ({ value, row: { original } }) => (original.documentExists ? (value ? "Да" : "Нет") : null),
            },
        ];
    }

    renderCreateDocumentButton = (item) => (
        <div
            className="btn btn-xs btn-outline-primary"
            onClick={() =>
                openUniversalTransferDocumentCreateForm({
                    item,
                    modal: this.props.modal,
                    roles: this.props.roles,
                    refresh: () => this.refreshAfterDocumentCreate(item),
                })
            }
        >
            Создать УПД
        </div>
    );

    renderCounter = () => <DocumentListCounter count={this.props.count} />;

    componentWillUnmount() {
        this.isUnmounted = true;
        this.clearDocumentUpdateSubscriptions();
    }

    refreshList = () => {
        if (this.isUnmounted) {
            return;
        }

        this.props.getList(this.props.params);
    };

    removeDocumentUpdateSubscription = (key) => {
        delete this.documentUpdateSubscriptions[key];
    };

    clearDocumentUpdateSubscription = (key) => {
        const subscription = this.documentUpdateSubscriptions[key];

        if (!subscription) {
            return;
        }

        delete this.documentUpdateSubscriptions[key];
        subscription.close();
    };

    clearDocumentUpdateSubscriptions = () => {
        Object.keys(this.documentUpdateSubscriptions).forEach(this.clearDocumentUpdateSubscription);
    };

    subscribeToDocumentUpdates = (item) => {
        if (this.isUnmounted) {
            return;
        }

        const key = getDocumentUpdateSubscriptionKey(item);

        this.clearDocumentUpdateSubscription(key);

        const subscription = subscribeToDocumentUpdates({
            item,
            onUpdate: this.refreshList,
            onClose: this.removeDocumentUpdateSubscription,
        });

        if (subscription) {
            this.documentUpdateSubscriptions[subscription.key] = subscription;
        }
    };

    refreshAfterDocumentCreate = (item) => {
        if (this.isUnmounted) {
            return;
        }

        this.subscribeToDocumentUpdates(item);
        this.refreshList();
    };

    renderListComponent = () => (
        <SharedReactTable
            {...this.props}
            withoutPagination
            isLoadingList={this.props.isLoadingList || this.state?.loading}
            columns={withDisabledTableControls(this.state.tableColumns)}
            getList={(params) => this.props.getList(params)}
        />
    );

    close = () => this.props.history.push(paths.finances);

    renderHead = () => (
        <div className="book-list__head-wrap">
            <div className="book-list__head">
                <div className="book-list__head-close close close--sm" onClick={this.close} />
                <div className="book-list__head-title">УПД{this.renderCounter()}</div>
                <div className="btn-toolbar">{this.props.canEditBooks && this.renderCreateButton()}</div>
            </div>
        </div>
    );

    openCreateForm = () => {
        this.props.modal({
            name: "default",
            title: "Создать УПД",
            windowMod: "wide",
            CustomBody: CreateUniversalTransferDocumentForm,
            bodyData: { onSuccess: () => this.props.mergeParams({}) },
        });
    };

    renderCreateButton = () => (
        <div className="btn btn-brand btn-sm" onClick={this.openCreateForm}>
            Создать УПД
        </div>
    );

    renderActions() {
        const { mergeParams } = this.props;
        const actions = [{ value: "Обновить", cname: "refresh", active: true, action: mergeParams, params: {} }];
        return actions.map((item, index) => <InlineBtn key={index} {...item} />);
    }

    render() {
        return (
            <div className="page-content">
                <Transition dir="full">
                    <div className="page-book__list book-list list">
                        {this.renderHead()}
                        <DocumentListFilters params={this.props.params} mergeParams={this.props.mergeParams} />
                        <div className="container-fluid my-2">{this.renderActions()}</div>
                        {this.renderListComponent()}
                    </div>
                </Transition>
            </div>
        );
    }
}

export default tableContainer(UniversalTransferDocumentsList, "finances-universal-transfer-documents");
